import { colors } from '../../../theme/tokens/colors';
import { chat as defaultAccent, surfaces, text, interactive, borders } from '../../../theme/tokens/semantic';
import { shadows as defaultShadows } from '../../../theme/tokens/shadows';
import { typography } from '../../../theme/tokens/typography';

const PAGE_MIN_HEIGHT = 'calc(100vh - 80px)';

/**
 * @param {{ accent?: object, shadows?: object }} [tokenOverrides]
 */
export function createDashboardHubSx(tokenOverrides = {}) {
  const accent = tokenOverrides.accent ?? defaultAccent;
  const shadowTokens = tokenOverrides.shadows ?? defaultShadows;

  return {
    pageRoot: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: PAGE_MIN_HEIGHT,
      bgcolor: surfaces.background,
      px: { xs: 2, md: 3 },
      py: 2.5,
      gap: 3,
    },
    welcomeSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
    },
    welcomeTitle: {
      fontWeight: typography.fontWeightBold,
      fontSize: typography.fontSize2xl ?? '24px',
      color: accent.textPrimary ?? text.primary,
      lineHeight: 1.25,
      letterSpacing: '-0.02em',
    },
    welcomeSubtitle: {
      fontSize: typography.fontSizeMd,
      color: accent.textSecondary ?? text.secondary,
      lineHeight: 1.5,
      maxWidth: 640,
    },
    quickLinksSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
    },
    quickLinkGrid: {
      width: '100%',
    },
    sectionTitle: {
      fontWeight: typography.fontWeightSemibold,
      fontSize: typography.fontSizeLg ?? '16px',
      color: accent.textPrimary ?? text.primary,
      lineHeight: 1.3,
    },
    quickLinkCard: {
      height: '100%',
      minHeight: 152,
      borderRadius: 0,
      border: `1px solid ${borders.border ?? accent.divider ?? colors.grey[200]}`,
      boxShadow: 'none',
      bgcolor: surfaces.surface ?? colors.white,
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
      '&:hover': {
        borderColor: accent.primary ?? interactive.primary,
        boxShadow: shadowTokens.xs,
      },
    },
    quickLinkAction: {
      height: '100%',
      alignItems: 'stretch',
      borderRadius: 0,
      '&:hover': {
        bgcolor: accent.primaryMuted ?? surfaces.surfaceSubtle,
      },
    },
    quickLinkContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 1.5,
      p: 2.5,
      height: '100%',
      '&:last-child': {
        pb: 2.5,
      },
    },
    quickLinkIconWrap: {
      width: 42,
      height: 42,
      borderRadius: 0,
      bgcolor: accent.primaryMuted ?? surfaces.surfaceSubtle,
      color: accent.primary ?? interactive.primary,
      border: `1px solid ${accent.primaryBorder ?? borders.border}`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    quickLinkTitle: {
      fontWeight: typography.fontWeightBold,
      fontSize: typography.fontSizeMd,
      color: accent.textPrimary ?? text.primary,
      lineHeight: 1.35,
    },
    quickLinkDescription: {
      fontSize: typography.fontSizeSm,
      color: accent.textSecondary ?? text.secondary,
      lineHeight: 1.5,
      flex: 1,
      minHeight: 40,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    recentActivitySection: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
    },
    viewAllRow: {
      display: 'flex',
      justifyContent: 'center',
      pt: 0.5,
    },
    viewAllLink: {
      border: 'none',
      bgcolor: 'transparent',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: typography.fontSizeSm,
      fontWeight: typography.fontWeightSemibold,
      color: accent.primary ?? interactive.primary,
      p: 0.5,
      transition: 'color 140ms ease',
      '&:hover': {
        color: accent.primaryDark ?? interactive.primary,
        textDecoration: 'underline',
      },
      '&:focus-visible': {
        outline: `2px solid ${accent.primary ?? interactive.primary}`,
        outlineOffset: 2,
      },
    },
    emptyState: {
      py: 4,
      px: 3,
      textAlign: 'center',
      borderRadius: 0,
      border: `1px dashed ${borders.border ?? accent.divider ?? colors.grey[300]}`,
      bgcolor: surfaces.surface ?? colors.white,
      color: accent.textSecondary ?? text.secondary,
    },
  };
}
